import * as React from "react";
import {
  compose,
  MutationFunc,
  graphql,
  withShowFlag,
  FlagProps
} from "@atlassiansox/microscopekit";
import * as Palette from "../../../../palette";
import { withMountTracking } from "../../../../core/components/mount-tracker";
import AddMicrosEnvironmentVariableMutation from "../models/add-micros-environment-variable.mutation.graphql";
import { getErrorMessage, isForbiddenError } from "../../../../core/helpers";
import {
  addMicrosEnvironmentVariable,
  addMicrosEnvironmentVariableVariables
} from "../models/__generated__/addMicrosEnvironmentVariable";
import { Tc2Error } from "../../../../core/components/tc2";

const AkSpinner = require("@atlaskit/spinner").default;

type AddStashKeyModalProps = {
  serviceId: string;
  environments: string[];
  closeModal: () => void;
  addMicrosEnvironmentVariable: MutationFunc<
    addMicrosEnvironmentVariable,
    addMicrosEnvironmentVariableVariables
  >;
} & FlagProps;

interface State {
  waiting: boolean;
  addError?: string;
  keyInput: string;
  valueInput: string;
  environmentInput: string;
}

export class AddStashKeyModalView extends React.Component<
  AddStashKeyModalProps,
  State
> {
  constructor(props: AddStashKeyModalProps) {
    super(props);

    this.state = {
      waiting: false,
      keyInput: "",
      valueInput: "",
      environmentInput: this.props.environments[0]
    };
  }

  addStashKey = async () => {
    if (this.state.keyInput === "") {
      this.setState({ addError: "Key must not be an empty string" });
      return;
    }
    this.setState({ waiting: true });
    try {
      await this.props.addMicrosEnvironmentVariable({
        variables: {
          service: this.props.serviceId,
          env: this.state.environmentInput,
          key: this.state.keyInput,
          value: this.state.valueInput
        }
      });
      this.setState({
        waiting: false,
        addError: undefined,
        keyInput: "",
        valueInput: ""
      });
      track("StashKeys.AddStashKeyModal: Add", { category: "info" });
      this.props.closeModal();
    } catch (error) {
      track("StashKeys.AddStashKeyModal: AddFailed", { category: "error" });
      const errMessage = getErrorMessage(error);
      let addError = errMessage;

      if (isForbiddenError(errMessage)) {
        const errComponent = <Tc2Error serviceId={this.props.serviceId} />;
        this.props.showFlag({
          type: "error",
          title: "Failed to add stash key",
          message: errComponent,
          disableAutoDismiss: true
        });
        addError = "403 Error";
      }

      this.setState({ waiting: false, addError });
    }
  };

  onKeyChange = (event: any) => {
    this.setState({ keyInput: event.target.value, addError: undefined });
  };

  onEnvironmentChange = (event: any) => {
    this.setState({
      environmentInput: event.target.value,
      addError: undefined
    });
  };

  onValueChange = (event: any) => {
    this.setState({ valueInput: event.target.value, addError: undefined });
  };

  render() {
    return (
      <Palette.Modal
        heading="Add Stash Key"
        actions={[
          {
            text: "Add",
            onClick: this.addStashKey,
            isDisabled: this.state.waiting,
            iconBefore: this.state.waiting ? (
              <AkSpinner size="small" isCompleting={false} />
            ) : (
              undefined
            )
          },
          {
            text: "Cancel",
            onClick: this.props.closeModal,
            isDisabled: this.state.waiting
          }
        ]}
        onClose={this.props.closeModal}
      >
        <Palette.Form onSubmit={() => this.addStashKey()} inline={true}>
          <Palette.SelectField
            required
            isDisabled={this.state.waiting}
            values={this.props.environments}
            value={this.state.environmentInput}
            onChange={this.onEnvironmentChange}
          />
          <Palette.TextField
            required
            placeholder="Key"
            isDisabled={this.state.waiting}
            value={this.state.keyInput}
            onChange={this.onKeyChange}
            error={this.state.addError}
          />
          <Palette.TextField
            required
            placeholder="Value"
            isDisabled={this.state.waiting}
            value={this.state.valueInput}
            onChange={this.onValueChange}
            error={this.state.addError}
          />
        </Palette.Form>
      </Palette.Modal>
    );
  }
}

export const AddStashKeyModal = compose(
  graphql(AddMicrosEnvironmentVariableMutation, {
    name: "addMicrosEnvironmentVariable"
  }),
  withShowFlag,
  withMountTracking("AddStashKeyModal")
)(AddStashKeyModalView);
